import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('pm-counter-animation')
export class PmCounterAnimation extends LitElement {
  @property({ type: Number }) target: number = 0;
  @property({ type: Number }) duration: number = 2000;
  @property({ type: Number }) threshold: number = 100;
  @property({ type: String }) suffix: string = '';
  @property({ type: String }) prefix: string = '';
  
  @state() private _current: number = 0;
  @state() private _observer: IntersectionObserver | null = null;
  @state() private _animated: boolean = false;

  static styles = css`
    :host {
      display: inline-block;
      font-weight: 700;
      font-size: 2.5rem;
      color: var(--bs-primary, #0d6efd);
      font-variant-numeric: tabular-nums;
    }

    .counter-content {
      transition: transform 0.3s ease;
    }

    :host(.animating) .counter-content {
      transform: scale(1.05);
    }

    @media (max-width: 768px) {
      :host {
        font-size: 2rem;
      }
    }
  `;

  constructor() {
    super();
    this.target = 0;
    this.duration = 2000;
    this.threshold = 100;
    this.suffix = '';
    this.prefix = '';
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._setupObserver();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._cleanupObserver();
  }

  private _setupObserver(): void {
    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this._animated) {
              this._startAnimation();
            }
          });
        },
        {
          rootMargin: `${this.threshold}px`,
          threshold: 0.1
        }
      );

      this._observer.observe(this as Element);
    } else {
      // Fallback for older browsers
      this._setupScrollListener();
    }
  }

  private _setupScrollListener(): void {
    let ticking = false;
    
    const checkVisibility = (): void => {
      if (this._animated) return;
      
      const rect = this.getBoundingClientRect();
      const isVisible = (
        rect.top >= 0 &&
        rect.top <= (window.innerHeight - this.threshold)
      );

      if (isVisible) {
        this._startAnimation();
      }
    };

    const onScroll = (): void => {
      if (!ticking) {
        requestAnimationFrame(() => {
          checkVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);
    
    // Initial check
    setTimeout(checkVisibility, 100);
  }

  private _cleanupObserver(): void {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  private _startAnimation(): void {
    if (this._animated || this.target <= 0) return;

    this._animated = true;
    this.classList.add('animating');

    const increment = this.target / (this.duration / 16); // 60fps
    let current = 0;

    const updateCounter = (): void => {
      current += increment;
      
      if (current < this.target) {
        this._current = Math.floor(current);
        this.requestUpdate();
        requestAnimationFrame(updateCounter);
      } else {
        this._current = this.target;
        this.classList.remove('animating');
        this.requestUpdate();

        // Dispatch completion event
        this.dispatchEvent(new CustomEvent('pm-counter:complete', {
          bubbles: true,
          composed: true,
          detail: {
            target: this.target,
            element: this
          }
        }));
      }
    };

    updateCounter();

    // Disconnect observer after animation
    if (this._observer) {
      this._observer.disconnect();
    }
  }

  /**
   * Public method to manually trigger animation
   */
  public startCounter(): void {
    this._startAnimation();
  }

  /**
   * Public method to reset counter
   */
  public reset(): void {
    this._animated = false;
    this._current = 0;
    this.classList.remove('animating');
    this.requestUpdate();
    
    if (this._observer) {
      this._observer.observe(this as Element);
    }
  }

  /**
   * Public method to set target and animate
   */
  public setTarget(target: number, startAnimation: boolean = true): void {
    this.target = target;
    if (startAnimation && !this._animated) {
      this._startAnimation();
    }
  }

  render() {
    return html`
      <span class="counter-content">
        ${this.prefix}${this._current.toLocaleString()}${this.suffix}
      </span>
    `;
  }
}

// Export for use in other modules
export default PmCounterAnimation;